import { useEffect } from 'react';
import { ROLES } from '@/constants/roles';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { activityApi } from '../api/activity.api';

/** How often the app reports in. The server counts at most a minute per report. */
const HEARTBEAT_MS = 60_000;

/**
 * How long without a key press, click, scroll or touch before somebody counts
 * as away. A tab left open on a desk overnight is not time spent in the app.
 */
const IDLE_MS = 5 * 60_000;

/** Interaction that proves somebody is there. Passive, so scrolling stays smooth. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'mousemove'] as const;

/**
 * Reports a minute of use while the app is open, visible and being used.
 *
 * Mounted once, at the root. Feeds the platform dashboard's "time in the app";
 * nothing about *what* the person is doing is sent — the request has no body.
 * Platform staff are not counted, so their console never reports in.
 *
 * Failures are dropped without a word: a missed minute is not worth an error on
 * anybody's screen, and the next report tries again.
 */
export function useActivityHeartbeat() {
  const role = useAuthStore((state) => state.user?.role);
  const userUuid = useAuthStore((state) => state.user?.uuid);
  const isTracked =
    userUuid !== undefined && role !== ROLES.SUPER_ADMIN && role !== ROLES.PLATFORM_SUPPORT;

  useEffect(() => {
    if (!isTracked) return;

    let lastInteraction = Date.now();
    const markActive = () => {
      lastInteraction = Date.now();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, markActive, { passive: true });
    }

    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastInteraction > IDLE_MS) return;
      activityApi.heartbeat().catch(() => undefined);
    }, HEARTBEAT_MS);

    return () => {
      window.clearInterval(timer);
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, markActive);
    };
    // Restarted for a different account, so one person's minutes never carry
    // over to whoever signs in next on the same machine.
  }, [isTracked, userUuid]);
}
